import { Component, computed, effect, inject, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { AppEnvironment } from 'sham-components/services/core/app-environment';
import { ShamRatingStarIcon } from 'sham-components/icons';
import { ProductReviewsManager } from '@/app/pages/product-details/product-reviews/product-reviews-manager';
import { ContentApi } from '@/app/services/content-api';

@Component({
  selector: 'product-reviews',
  standalone: true,
  imports: [DecimalPipe, ShamRatingStarIcon, TranslateModule],
  templateUrl: './product-reviews-page.html',
})
export class ProductReviewsPage {
  private readonly route = inject(ActivatedRoute);
  private readonly reviewsManager = inject(ProductReviewsManager);
  private readonly contentApi = inject(ContentApi);
  readonly appEnv = inject(AppEnvironment);

  // Slug read from the route (param key is still 'productId' in paramMap,
  // but the value passed via routerLink is now productSlug()).
  private productSlug = signal<string>('');
  // Real product guid, resolved after fetching product-by-slug.
  // All review API calls (which require a real id) use this, not the slug.
  private productId = signal<string>('');

  isLoading = computed(() => this.reviewsManager.loading());
  reviews = computed(() => this.reviewsManager.reviewsList());
  summary = computed(() => this.reviewsManager.reviewSummary());
  currentPage = computed(() => this.reviewsManager.currentPage());
  totalPages = computed(() => this.reviewsManager.totalPages());

  showFilterDropdown = signal(false);
  selectedFilter = signal<number | null>(null);

  showPageDropdown = signal(false);
  pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1),
  );

  constructor() {
    effect(() => {
      const slug = this.route.snapshot.paramMap.get('productId');
      if (slug) {
        this.productSlug.set(slug);
        this.loadProductAndReviews(slug);
      }
    });
  }

  private async loadProductAndReviews(slug: string) {
    try {
      const product = await firstValueFrom(
        this.contentApi.getProductBySlug(slug, 'en'),
      );
      if (!product) return;

      this.productId.set(product.id);
      this.reviewsManager.setSummary(product.reviewsAverage, product.reviewsCount);

      this.loadReviews(product.id, 1);
    } catch (error) {
      console.error('Error loading product details:', error);
    }
  }

  private loadReviews(productId: string, page: number) {
    const rating = this.selectedFilter();
    const filter = rating ? { rating } : undefined;
    firstValueFrom(this.reviewsManager.loadProductReviews(productId, page, filter))
      .then((response) => this.reviewsManager.setReviewsData(response))
      .catch((error) => console.error('Error loading reviews:', error));
  }

  getStarArray(rating: number): number[] {
    const filled = Math.max(0, Math.min(5, Math.round(rating)));
    return Array(filled).fill(0);
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  formatDate(date: string | Date): string {
    return new Date(date).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  filterByRating(rating: number | null): void {
    this.selectedFilter.set(rating);
    this.showFilterDropdown.set(false);
    this.reviewsManager.applyFilter(rating ? { rating } : {});
    const id = this.productId();
    if (id) this.loadReviews(id, 1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.showPageDropdown.set(false);
    const id = this.productId();
    if (id) this.loadReviews(id, page);
  }

  goToPreviousPage(): void {
    const current = this.currentPage();
    if (current > 1) this.goToPage(current - 1);
  }

  goToNextPage(): void {
    const current = this.currentPage();
    if (current < this.totalPages()) this.goToPage(current + 1);
  }
}
